import './timed-message.css';

import { Util } from '../../../utils/util';

/**
 * A class for displaying temporary messages that automatically disappear after a set duration.
 */
export class TimedMessage {
    private readonly _element: HTMLDialogElement | null = null;
    private readonly _timeout: number = 1000;

    /**
     * Creates an instance of TimedMessage.
     * @param {string} message - The main message to display.
     * @param {string} [caption] - Optional caption displayed below the message.
     * @param {number} [timeout=1000] - The time (in milliseconds) before the message disappears.
     */
    constructor(message: string, caption?: string, timeout?: number, screenReaderMsg?: string) {
        this._element = this._createElement(message, caption, screenReaderMsg);
        this._element.addEventListener("touchmove", this._preventTouch, { passive: false });
        if (timeout)
            this._timeout = timeout;
    }

    private readonly _preventTouch = (e: TouchEvent): void => {
        e.preventDefault();
        e.stopPropagation();
    };

    /**
     * Displays the message on the screen and removes it after the specified timeout.
     */
    public show(): void {
        if (this._element) {
            document.body.appendChild(this._element);

            this._element.showModal();
            this._element.classList.add("on");

            setTimeout(() => {
                this._destroy();
            }, this._timeout);
        }
    }

    /**
     * Creates the HTML element for the message display.
     * @param {string} message - The main message to display.
     * @param {string} [caption] - Optional caption displayed below the message.
     * @param {string} [screenReaderMsg] - Optional message for screen readers. It will replace the visual message for accessibility.
     * @returns {HTMLElement} - The constructed HTML element for the message.
     */
    private _createElement(message: string, caption?: string, screenReaderMsg?: string): HTMLDialogElement {
        const dialog = this._createDialog(screenReaderMsg);

        if (screenReaderMsg) {
            const screenReaderDiv = this._createScreenReaderDiv(screenReaderMsg);
            dialog.appendChild(screenReaderDiv);
        }

        const mainDiv = this._createMainDiv(message, screenReaderMsg);
        dialog.appendChild(mainDiv);

        if (caption) {
            const captionDiv = this._createCaptionDiv(caption, screenReaderMsg);
            dialog.appendChild(captionDiv);
        }

        return dialog;
    }

    private _createDialog(screenReaderMsg?: string): HTMLDialogElement {
        const dialog = document.createElement("dialog");
        dialog.id = `timed_message_${Util.generateHex(4)}`;
        dialog.className = "timed-message";
        dialog.setAttribute("closedby", "none");
        dialog.setAttribute("aria-live", "assertive");
        return dialog;
    }

    private _createScreenReaderDiv(screenReaderMsg: string): HTMLDivElement {
        const srDiv = document.createElement("div");
        srDiv.className = "screen-reader-only"; // CSS class to hide visually but keep for screen readers
        srDiv.textContent = screenReaderMsg;
        return srDiv;
    }

    private _createMainDiv(message: string, screenReaderMsg?: string): HTMLDivElement {
        const mainDiv = document.createElement("div");
        mainDiv.className = "main";
        mainDiv.textContent = message;
        if (screenReaderMsg) { // Hide visual message from screen readers if a separate message is provided
            mainDiv.setAttribute("aria-hidden", "true");
        }
        return mainDiv;
    }

    private _createCaptionDiv(caption: string, screenReaderMsg?: string): HTMLDivElement {
        const captionDiv = document.createElement("div");
        captionDiv.className = "caption";
        captionDiv.textContent = caption;
        if (screenReaderMsg) { // Hide visual caption from screen readers if a separate message is provided
            captionDiv.setAttribute("aria-hidden", "true");
        }
        return captionDiv;
    }

    /**
     * Fades out and removes the message from the DOM after a specified duration.
     * @param {number} [duration=250] - The duration (in milliseconds) of the fade-out effect.
     */
    private readonly _destroy = (duration: number = 250): void => {
        if (this._element && this._element.parentNode) {
            this._element.classList.toggle("on", false);

            setTimeout(() => {
                if (this._element?.parentNode) {
                    this._element.close();
                    this._element.parentNode.removeChild(this._element);
                }
            }, duration);
        }
    }
}